import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-normal text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline'
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3',
        lg: 'h-10 px-8',
        full: 'h-9 w-full py-2',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10'
      },
      radius: {
        default: 'rounded-md',
        none: 'rounded-none',
        sm: 'rounded-sm',
        lg: 'rounded-lg',
        full: 'rounded-full'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      radius: 'default'
    }
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  icon?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, radius, loading = false, icon = false, children, ...props },
    ref
  ) => {
    const buttonClass = cn(
      buttonVariants({ variant, size, radius }),
      icon ? 'w-9 px-0 py-0' : '',
      className
    );

    return (
      <button className={buttonClass} ref={ref} {...props} disabled={loading || props.disabled}>
        {loading && icon ? null : children}
        {loading && (
          <Loader2
            data-testid="loader-icon"
            className={cn('h-4 w-4 animate-spin', children && !icon && 'ml-2')}
          />
        )}
      </button>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
